import { useMemo } from 'react';
import { generatePath, Redirect } from 'react-router-dom';

import { INVALID_START_SECTIONS, SECTION } from 'src/constants/section';
import type { ModulePermission } from 'src/types/modulePermission';
import { getModulePath } from 'src/helper/helper';
import { storedAuthToken } from 'src/services/api/http';
import { routePaths } from 'src/services/routing/routePaths';
import { useUserContext } from 'src/components/app/context/UserContext';
import PageFrame from 'src/components/common/PageFrame';
import PageNotFound from 'src/components/common/PageNotFound';

import useUserPermissions from '../context/UserContext/useUserPermissions';

/**
 * Returns the matching module path for the first user permission module id.
 */
const getStartPath = (
  activeUserPermissionsInCurrentSurvey: readonly ModulePermission[],
  adminUserPermissions: readonly ModulePermission[],
) => {
  if (
    activeUserPermissionsInCurrentSurvey.length === 0 &&
    adminUserPermissions.length === 0
  )
    return;

  // My follow-up isn't "inside" a survey and shouldn't be redirected to
  const filteredModules = activeUserPermissionsInCurrentSurvey.filter(
    ({ module }) =>
      module.hide_tab
        ? !module.hide_tab
        : !INVALID_START_SECTIONS.includes(module.module_type),
  );
  const firstModule = filteredModules.length ? filteredModules[0].module : null;

  const route = firstModule
    ? getModulePath(firstModule.module_type)
    : adminUserPermissions.length
      ? getModulePath(SECTION.USER_ADMIN)
      : undefined;

  if (!route) return;

  const params =
    route === routePaths.DASHBOARD_DETAIL
      ? {
          slug: firstModule?.slug || SECTION.USER_ADMIN,
        }
      : undefined;

  return generatePath(route, params);
};

const StartPage = () => {
  const { user } = useUserContext();
  const { activeUserPermissionsInCurrentSurvey, adminUserPermissions } =
    useUserPermissions();
  const startPath = useMemo(
    () =>
      getStartPath(activeUserPermissionsInCurrentSurvey, adminUserPermissions),
    [activeUserPermissionsInCurrentSurvey, adminUserPermissions],
  );

  if (startPath && storedAuthToken()) {
    return (
      <PageFrame>
        <Redirect to={startPath} />
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      {user ? <PageNotFound /> : <div data-test-id="waiting" />}
    </PageFrame>
  );
};

export default StartPage;
